import React, { useCallback, useRef, useMemo, Suspense } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, CircularProgress } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

// Lazy load AceEditor to avoid loading it for nodes that don't need code editing
const AceEditor = React.lazy(() =>
  import('react-ace').then((mod) => {
    // Pre-load both themes so Ace can switch without flicker
    import('ace-builds/src-noconflict/theme-github');
    import('ace-builds/src-noconflict/theme-monokai');
    return mod;
  })
);

/**
 * NodeCodeEditor — AceEditor wrapper for code, cURL, JSON editing on nodes.
 * Lazy-loaded to avoid performance impact on nodes that don't use it.
 *
 * Usage:
 *   <NodeCodeEditor
 *     label="Code"
 *     value={code}
 *     onChange={(val) => setCode(val)}
 *     language="javascript"
 *     height="200px"
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const EditorContainer = styled('div')(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  overflow: 'hidden',
  '& .ace_editor, & .ace_editor *': {
    fontFamily: '"Monaco", "Menlo", "Consolas", "Courier New", monospace !important',
    fontSize: '12px !important',
    lineHeight: 'normal !important',
  },
}));

const LoadingFallback = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: NODE_UI.spacing.xl,
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.value,
  gap: NODE_UI.spacing.sm,
}));

const NodeCodeEditor = React.memo(function NodeCodeEditor({
  label,
  value = '',
  onChange,
  language = 'javascript',
  height = '180px',
  readOnly = false,
  placeholder = '',
  showGutter = true,
  showPrintMargin = false,
  wrapEnabled = true,
  sx,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const editorRef = useRef(null);
  const editorId = useMemo(() => `node-code-editor-${Math.random().toString(36).slice(2, 8)}`, []);

  const handleChange = useCallback((newValue) => {
    onChange?.(newValue);
  }, [onChange]);

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <EditorContainer sx={sx}>
        <Suspense
          fallback={
            <LoadingFallback style={{ height }}>
              <CircularProgress size={14} />
              Loading editor...
            </LoadingFallback>
          }
        >
          <AceEditor
            ref={editorRef}
            mode={language}
            theme={isDark ? 'monokai' : 'github'}
            value={value}
            onChange={handleChange}
            name={editorId}
            width="100%"
            height={height}
            fontSize={12}
            showGutter={showGutter}
            showPrintMargin={showPrintMargin}
            wrapEnabled={wrapEnabled}
            readOnly={readOnly}
            placeholder={placeholder}
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              useWorker: false,
              tabSize: 2,
              fontFamily: 'Monaco, Menlo, Consolas, Courier New, monospace',
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: false,
              showLineNumbers: showGutter,
            }}
          />
        </Suspense>
      </EditorContainer>
    </div>
  );
});

export default NodeCodeEditor;
